import {Colors, FInter, FlexBox, useIsMobile} from "@brg/ui";
import {useTranslation} from "react-i18next";
import {
    CompleteTitle,
    CompleteWrapper,
    NoProjectsDescription,
    NoProjectsWrapper,
    SendProjectModalWrapper
} from "../sendProjects/employStyles";
import {FC, useCallback, useMemo, useState} from "react";
import {API, Group, GroupAPI, Project, Team} from "@brg/api";
import {useProfile} from "../../../core/hooks/auth/useProfile";
import {SortingTypeIndex} from "../../constants/sortingType";
import {CreateLinkTo} from "../../constants/linkTo";
import {useHistory} from "react-router-dom";
import {useModal} from "../../../core";
import { ModalContainer } from "../sendProjects/styles";
import { DesignersGridNew } from "shared/components/grids";
import { DesignerCardNew } from "shared/components/designerCard/designerCard";
import { computeDesigner } from "shared/helpers/computeData";
import { IDesignerLight } from "shared/common";
import { BackButtonWrapper } from "./styles";
import { computeGroup } from "shared/helpers/types/computed/ComputedGroup";
import { GroupHeader } from "./components/GroupHeader";
import { MagicBoxRow } from "shared/components/MagicBoxRow";
import { HoverItemText, HoverItemWithSvg } from "shared/components/projectsSearchBar/style";
import { useTheme } from "styled-components";
import { AppTester } from "supporting";
import { SendButton } from "shared/pages/ModeratePages/styles";


const NoMembers: FC<{title: string}> = ({title}) => {
    const {t} = useTranslation();

    return (<CompleteWrapper>
        <NoProjectsWrapper>
            <CompleteTitle>
                {t('teams.modal.no_group_members', {title})}
            </CompleteTitle>
            <NoProjectsDescription>
                {t('teams.modal.no_group_members', {title})}
            </NoProjectsDescription>
        </NoProjectsWrapper>
    </CompleteWrapper>)
}

const ActionButtons: FC<{ onPrimaryClick: () =>  void, disable?: boolean, showAddButton?: boolean, count?: number }> = ({ onPrimaryClick, disable, showAddButton, count }) => {
    const { t } = useTranslation();

    return (
        <FlexBox width={'100%'} justify="end" justifyMobile="end" gx={24} gxMobile={24} style={{ opacity: disable ? 0 : 1 }} offsetBottom={40}>
            <SendButton onClick={onPrimaryClick}>{t('teams.modal.action_buttons.secondary', {count})}</SendButton>
        </FlexBox>
    )
}

export const BackButton: FC<{ onClick: () =>  void, title: string }> = ({ onClick, title }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <BackButtonWrapper onClick={onClick}>
            <HoverItemWithSvg color={theme.sortByColorSort} gx={8}>
                <FInter.SmallText style={{alignItems: 'center', display: 'flex'}}>
                    <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 6.15801L1.25981 0.223468C0.971612 -0.0744925 0.504346 -0.0744886 0.216147 0.223475C-0.0720515 0.52144 -0.0720488 1.00453 0.216154 1.30249L6.47817 7.77653C6.76637 8.07449 7.23363 8.07449 7.52183 7.77653L13.7838 1.30249C14.072 1.00453 14.0721 0.52144 13.7839 0.223475C13.4957 -0.0744886 13.0284 -0.0744925 12.7402 0.223468L7 6.15801Z"
                                fill={theme.tags.textColor}/>
                    </svg>
                </FInter.SmallText>
                <HoverItemText className={'small-text'}>{t('teams.modal.back', {title})}</HoverItemText>
            </HoverItemWithSvg>
        </BackButtonWrapper>
    )
}

export const GroupModal: FC<{group: Group<'Global'>, team?: Team, onUpdate?: (val: number) => void, isCanEditGroup?: boolean}> = ({group, team, onUpdate, isCanEditGroup}) => {
    const isMobile = useIsMobile();
    const { t, i18n } = useTranslation();
    const profile = useProfile();
    const modal = useModal();
    const history = useHistory();
    const [sorting, setSorting] = useState(1);
    const [selected, setSelected] = useState<string[]>([])
    const [isSendingCompleted, setIsSendingCompleted] = useState(false);
    const isHseDesign = AppTester.isHSEDesign();

    //@ts-ignore
    const computed= useMemo(() => (!!group && computeGroup(API.toLang(group , i18n.language))), [group, i18n]);
    // @ts-ignore
    const members: IDesignerLight[] = useMemo(() => computed && computed?.members?.map((item: any) => computeDesigner(item)) || [], [computed]);

    const title = useMemo(() => (team?.name || ''), [team]);

    const onSortingChange = useCallback((s: SortingTypeIndex): void => {
        setSorting(s);
    }, []);

    const isEnoughMembers= useMemo(() =>  {
        return !!members?.length
    },[members])

    const onMobileSortingClick = useCallback(() => {
        setSorting(sorting === 1 ? 3 : 1);
    }, [sorting, setSorting]);

    const onAddNewMemebersClick = useCallback(() => {
        modal.hide();
        isHseDesign
            // @ts-ignore
            ? history.push(`/teams/${team?.contextName}/employees/moder`)
            : history.push('/teams/employees/moder');
    }, [team, modal])

    const onSelectMembers = (id: string) => {
        const find = selected.filter((i) => i === id)
        setSelected(!!find?.length ? selected.filter((i) => i !== id) : [...selected, id])
    } 

    const onDesignerClick = (id: string) => {
        window.open(CreateLinkTo.designer(id), '_blank');
    }

    const isSelected = (id:string) => selected?.includes(id)

    const chooseAll = () => 
        selected?.length === members?.length ? setSelected([]): setSelected(members?.map((member) => member?.designerId ));

    const onBackClick = () => modal.hide();

    const onDeleteMembersClick = (val?: boolean) => {
        const confirmation = window.confirm('Вы действительно хотите удалить выбранных участников?');
        if (confirmation) {
            const copy: Group<'Global'> = { ...group};
            const membersIds = members?.map((member) => member?.designerId)
            copy.membersIDs = membersIds?.filter((id, idx) => !selected?.includes(id));

            GroupAPI.Update(copy).then((r) => {
                setSelected([]);
                modal.hide();
                onUpdate && onUpdate(Date.now())
            })
        }
    }

    const headerComponent = useMemo(() => {
        if (!selected?.length) {
            return <BackButton onClick={onBackClick} title={title} />
        }

        return (
            <MagicBoxRow
                className={'checkbox'} 
                checked={selected?.length === members?.length}
                label={t('teams.modal.choose_all')}
                onClick={chooseAll}
                isTeamEdit
            />
        )
    }, [selected, title, t, isCanEditGroup])



    if (!profile) {
        return null;
    }


    return <ModalContainer style={{ overflow: 'auto', marginTop: 0 }}>
        <SendProjectModalWrapper offsetTop={isMobile ? (!isSendingCompleted && isEnoughMembers ? 0 : 40) : 0} width={'100%'} directionMobile='column' direction='column' justify={'flex-start'} align={"center"} style={{ position: 'relative'}}>
            <GroupHeader isGroupModal showAddButton={!!isCanEditGroup} leftBar={headerComponent} title={title} onMobileSortingClick={onMobileSortingClick} onSortingChange={onSortingChange} sorting={sorting} onBackClick={onBackClick} onAddNewMemebersClick={onAddNewMemebersClick}>
                <DesignersGridNew isLoading={false} mobileColumns={2}>
                    {members?.map((member) => (
                        <DesignerCardNew
                        isSelectable={!!isCanEditGroup}
                        alreadyProject
                        showOpa={false}
                        onSelect={onSelectMembers}
                        selected={isSelected(member?.designerId)}
                        onClick={() => onDesignerClick(member.designerId)}
                        {...member}
                        isGroupModal
                        key={member.designerId}
                        isOpaCard={false}
                    />
                    ))}
                </DesignersGridNew>
            </GroupHeader>
            {!isEnoughMembers  && <NoMembers title={computed?.name || ''}/>}
            {isEnoughMembers && !!isCanEditGroup && <ActionButtons onPrimaryClick={onDeleteMembersClick} disable={!selected?.length} count={selected?.length} showAddButton />}
        </SendProjectModalWrapper>
    </ModalContainer>
}import {Colors, FInter, FlexBox, useIsMobile} from "@brg/ui";
import {useTranslation} from "react-i18next";
import {
    CompleteTitle,
    CompleteWrapper,
    NoProjectsDescription,
    NoProjectsWrapper,
    SendProjectModalWrapper
} from "../sendProjects/employStyles";
import {FC, useCallback, useMemo, useState} from "react";
import {API, Group, GroupAPI, Project, Team} from "@brg/api";
import {useProfile} from "../../../core/hooks/auth/useProfile";
import {SortingTypeIndex} from "../../constants/sortingType";
import {CreateLinkTo} from "../../constants/linkTo";
import {useHistory} from "react-router-dom";
import {useModal} from "../../../core";
import { ModalContainer } from "../sendProjects/styles";
import { DesignersGridNew } from "shared/components/grids";
import { DesignerCardNew } from "shared/components/designerCard/designerCard";
import { computeDesigner } from "shared/helpers/computeData";
import { IDesignerLight } from "shared/common";
import { BackButtonWrapper } from "./styles";
import { computeGroup } from "shared/helpers/types/computed/ComputedGroup";
import { GroupHeader } from "./components/GroupHeader";
import { MagicBoxRow } from "shared/components/MagicBoxRow";
import { HoverItemText, HoverItemWithSvg } from "shared/components/projectsSearchBar/style";
import { useTheme } from "styled-components";
import { AppTester } from "supporting";
import { SendButton } from "shared/pages/ModeratePages/styles";


const NoMembers: FC<{title: string}> = ({title}) => {
    const {t} = useTranslation();

    return (<CompleteWrapper>
        <NoProjectsWrapper>
            <CompleteTitle>
                {t('teams.modal.no_group_members', {title})}
            </CompleteTitle>
            <NoProjectsDescription>
                {t('teams.modal.no_group_members', {title})}
            </NoProjectsDescription>
        </NoProjectsWrapper>
    </CompleteWrapper>)
}

const ActionButtons: FC<{ onPrimaryClick: () =>  void, disable?: boolean, showAddButton?: boolean, count?: number }> = ({ onPrimaryClick, disable, showAddButton, count }) => {
    const { t } = useTranslation();

    return (
        <FlexBox width={'100%'} justify="end" justifyMobile="end" gx={24} gxMobile={24} style={{ opacity: disable ? 0 : 1 }} offsetBottom={40}>
            <SendButton onClick={onPrimaryClick}>{t('teams.modal.action_buttons.secondary', {count})}</SendButton>
        </FlexBox>
    )
}

export const BackButton: FC<{ onClick: () =>  void, title: string }> = ({ onClick, title }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <BackButtonWrapper onClick={onClick}>
            <HoverItemWithSvg color={theme.sortByColorSort} gx={8}>
                <FInter.SmallText style={{alignItems: 'center', display: 'flex'}}>
                    <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 6.15801L1.25981 0.223468C0.971612 -0.0744925 0.504346 -0.0744886 0.216147 0.223475C-0.0720515 0.52144 -0.0720488 1.00453 0.216154 1.30249L6.47817 7.77653C6.76637 8.07449 7.23363 8.07449 7.52183 7.77653L13.7838 1.30249C14.072 1.00453 14.0721 0.52144 13.7839 0.223475C13.4957 -0.0744886 13.0284 -0.0744925 12.7402 0.223468L7 6.15801Z"
                                fill={theme.tags.textColor}/>
                    </svg>
                </FInter.SmallText>
                <HoverItemText className={'small-text'}>{t('teams.modal.back', {title})}</HoverItemText>
            </HoverItemWithSvg>
        </BackButtonWrapper>
    )
}

export const GroupModal: FC<{group: Group<'Global'>, team?: Team, onUpdate?: (val: number) => void, isCanEditGroup?: boolean}> = ({group, team, onUpdate, isCanEditGroup}) => {
    const isMobile = useIsMobile();
    const { t, i18n } = useTranslation();
    const profile = useProfile();
    const modal = useModal();
    const history = useHistory();
    const [sorting, setSorting] = useState(1);
    const [selected, setSelected] = useState<string[]>([])
    const [isSendingCompleted, setIsSendingCompleted] = useState(false);
    const isHseDesign = AppTester.isHSEDesign();

    //@ts-ignore
    const computed= useMemo(() => (!!group && computeGroup(API.toLang(group , i18n.language))), [group, i18n]);
    // @ts-ignore
    const members: IDesignerLight[] = useMemo(() => computed && computed?.members?.map((item: any) => computeDesigner(item)) || [], [computed]);

    const title = useMemo(() => (team?.name || ''), [team]);

    const onSortingChange = useCallback((s: SortingTypeIndex): void => {
        setSorting(s);
    }, []);

    const isEnoughMembers= useMemo(() =>  {
        return !!members?.length
    },[members])

    const onMobileSortingClick = useCallback(() => {
        setSorting(sorting === 1 ? 3 : 1);
    }, [sorting, setSorting]);

    const onAddNewMemebersClick = useCallback(() => {
        modal.hide();
        isHseDesign
            // @ts-ignore
            ? history.push(`/teams/${team?.contextName}/employees/moder`)
            : history.push('/teams/employees/moder');
    }, [team, modal])

    const onSelectMembers = (id: string) => {
        const find = selected.filter((i) => i === id)
        setSelected(!!find?.length ? selected.filter((i) => i !== id) : [...selected, id])
    } 

    const onDesignerClick = (id: string) => {
        window.open(CreateLinkTo.designer(id), '_blank');
    }

    const isSelected = (id:string) => selected?.includes(id)

    const chooseAll = () => 
        selected?.length === members?.length ? setSelected([]): setSelected(members?.map((member) => member?.designerId ));

    const onBackClick = () => modal.hide();

    const onDeleteMembersClick = (val?: boolean) => {
        const confirmation = window.confirm('Вы действительно хотите удалить выбранных участников?');
        if (confirmation) {
            const copy: Group<'Global'> = { ...group};
            const membersIds = members?.map((member) => member?.designerId)
            copy.membersIDs = membersIds?.filter((id, idx) => !selected?.includes(id));

            GroupAPI.Update(copy).then((r) => {
                setSelected([]);
                modal.hide();
                onUpdate && onUpdate(Date.now())
            })
        }
    }

    const headerComponent = useMemo(() => {
        if (!selected?.length) {
            return <BackButton onClick={onBackClick} title={title} />
        }

        return (
            <MagicBoxRow
                className={'checkbox'} 
                checked={selected?.length === members?.length}
                label={t('teams.modal.choose_all')}
                onClick={chooseAll}
                isTeamEdit
            />
        )
    }, [selected, title, t, isCanEditGroup])



    if (!profile) {
        return null;
    }


    return <ModalContainer style={{ overflow: 'auto', marginTop: 0 }}>
        <SendProjectModalWrapper offsetTop={isMobile ? (!isSendingCompleted && isEnoughMembers ? 0 : 40) : 0} width={'100%'} directionMobile='column' direction='column' justify={'flex-start'} align={"center"} style={{ position: 'relative'}}>
            <GroupHeader isGroupModal showAddButton={!!isCanEditGroup} leftBar={headerComponent} title={title} onMobileSortingClick={onMobileSortingClick} onSortingChange={onSortingChange} sorting={sorting} onBackClick={onBackClick} onAddNewMemebersClick={onAddNewMemebersClick}>
                <DesignersGridNew isLoading={false} mobileColumns={2}>
                    {members?.map((member) => (
                        <DesignerCardNew
                        isSelectable={!!isCanEditGroup}
                        alreadyProject
                        showOpa={false}
                        onSelect={onSelectMembers}
                        selected={isSelected(member?.designerId)}
                        onClick={() => onDesignerClick(member.designerId)}
                        {...member}
                        isGroupModal
                        key={member.designerId}
                        isOpaCard={false}
                    />
                    ))}
                </DesignersGridNew>
            </GroupHeader>
            {!isEnoughMembers  && <NoMembers title={computed?.name || ''}/>}
            {isEnoughMembers && !!isCanEditGroup && <ActionButtons onPrimaryClick={onDeleteMembersClick} disable={!selected?.length} count={selected?.length} showAddButton />}
        </SendProjectModalWrapper>
    </ModalContainer>
}